import React, { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Grid,
  Tooltip,
  Divider,
} from "@mui/material";
import { NoteData } from "../../../utils/types";
import Note from "./Note";
import useDisplaySnackbar from "../../../utils/useDisplaySnackbar";
import ProgressSpinner from "../../Common/ProgressSpinner";
import NoteTileComponent from "./NoteTileComponent";
import { getUserAttributes } from "../../../utils/auth";
import { useLocation } from "react-router-dom";
import "../../../styles/common.css"
import NotesService from "../../../services/notes/NoteService";
import SlideOver from "../../Common/SlideOver";
import PrepNotesSlideOver from "../../MeetingDetailsPage/PrepNotesSlideOver";
import PrepNotesCard from "../../MeetingDetailsPage/PrepNotesCard";
import useGlobalContext from "../../../context/useGlobalContext";
import UserInteractionsService from "../../../services/interactions/Interactions";
import addIcon from "../../../assets/interaction-detail-page/plus-icon.svg";
import NotesIcon from "../../../assets/interaction-detail-page/no-notes-icon.svg";
import NoteViewer from "./NoteViewer";
import CustomConfirmation from "../../Common/CustomConfirmation";

interface NotesProps {
  id: string;
  noteType?: "public" | "private";
  isInteractionAttendee?: boolean;
  hasMeetingTimePassed?: boolean;
}

const Notes: React.FC<NotesProps> = ({ id, isInteractionAttendee, hasMeetingTimePassed = false}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const is_team_meeting_id = true;
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const [publicNotes, setPublicNotes] = useState<NoteData[]>([]);
  const [userPublicNoteExists, setUserPublicNoteExists] =
    useState<boolean>(false);
  const loggedInUserEmail = getUserAttributes()?.email;
  const [noteToDelete, setNoteToDelete] = useState<NoteData | null>(null);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const selectedTabs = location.state?.selectedTab ?? params.get("st") ?? "";
  const sharedWithMe = location.state?.sharedWithMe ?? selectedTabs === "sharedWithMe"? true : false;
  const sharedByMe = location.state?.sharedByMe ?? selectedTabs === "sharedByMe"? true : false;
  const [expandPublicNote, setExpandPublicNote] = useState(false);
  const isClientInteraction = location.state?.isClientInteraction ?? params.has("ci");
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const { featureMap, impersonationDetails } = useGlobalContext();
  const [notes, setNotes] = useState<any[]>([]);
  const isPrepNotesEnabled = featureMap?.prep_notes === true;
  const [selectedNote, setSelectedNote] = useState<NoteData | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const impersonating = impersonationDetails?.isImpersonating === true;
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;

  const {
    isLoading: isMeetingNotesLoading,
    isFetching: isMeetingNotesFetching,
    isSuccess: isMeetingNotesSuccess,
    isError: isMeetingNotesError,
    error: MeetingNotesError,
    data: MeetingNotesData,
  } = NotesService.useGetInteractionNotes( 
    {
      interactionId: id || "",
      isTeamInteraction: is_team_meeting_id,
      sharedWithMe: sharedWithMe || undefined,
      sharedByMe: sharedByMe || undefined,
      isForOrg: isClientInteraction || undefined,
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    }, true
  );

    const 
    {
        isLoading: isNotesLoading,
        isFetching: isNotesFetching,
        isSuccess: isNotesSuccess, 
        isError: isNotesError,
        error: notesError, 
        data: notesData 
    } = UserInteractionsService.useGetInteractionNotes(
        {
            interactionId: id || "",
            impersonatingPorId: impersonatingUserPorId,
            accessLevel: accessLevel,
        }, true
    );

    useEffect(() => {
    setShowSpinner(isNotesLoading || isNotesFetching);
    if(isNotesSuccess && notesData) {
        setNotes(notesData?.interactions || []);
    }
    else if (isNotesError){
        showSnackbar("error", notesError || "Failed to fetch notes");
    }
    }, [isNotesLoading, isNotesFetching, isNotesSuccess, isNotesError, notesError, notesData]);

  const {
    isLoading: isPrepNotesLoading,
    isFetching: isPrepNotesFetching,
    isError: isPrepNotesError,
    error: PrepNotesError,
    data: PrepNotesData,
    refetch: refetchPrepNotes, 
  } = NotesService.useGetPrepNotes(id || "", !!id);
  const prepNotes = PrepNotesData?.data ?? null;

  const { mutate: generatePrepNotes,isPending: isGenerateLoading} =
  NotesService.useGeneratePrepNotesMutation({
    onSuccess: () => {
      refetchPrepNotes();
    },
    onError: (err) => {
      const errorMessage =
        err?.response?.data?.errors || "Failed to generate prep notes";
      showSnackbar("error", errorMessage);
    },
  });
  
  const { mutate: mutateNoteDelete } = NotesService.useDeleteNoteMutation(
    {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get_notes"],
        });
        showSnackbar("success", "Note deleted successfully");
      },
      onError: (error: string) => {
        showSnackbar("error", `Error while deleting note: ${error}`);
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    }
  );

  const { mutate: mutateNoteUpdate } = NotesService.useUpdateNoteMutation(
      {
        onMutate: () => {
          setShowSpinner(true);
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["get_notes"],
          });
          showSnackbar("success", "Note updated successfully");
        },
        onError: (error: string) => {
          showSnackbar("error", `Error while updating note: ${error}`);
        },
        onSettled: () => {
          setShowSpinner(false);
        },
      }
    );

  useEffect(() => {
    setUserPublicNoteExists(false);
    setPublicNotes([]);
    if (isMeetingNotesSuccess) {
      const publicNotes: NoteData[] = [];
      MeetingNotesData?.data?.forEach((note: any) => {
        if (!note) return;
        if (!note?.isPrivate) {
          publicNotes.push(note);
          note?.creatorEmail === loggedInUserEmail && setUserPublicNoteExists(true);
        }
      });
      setPublicNotes(publicNotes);
    }
    if (isMeetingNotesError) {
      const errorMessage = MeetingNotesError?.response?.data?.errors || "An unknown error occurred";
       showSnackbar("error", errorMessage);
    }
  }, [
    isMeetingNotesLoading,
    isMeetingNotesFetching,
    isMeetingNotesSuccess,
    isMeetingNotesError,
  ]);

  const handleDelete = () => {
    if (noteToDelete) mutateNoteDelete({ noteId: noteToDelete.id });
    setOpenDialog(false);
    setIsSlideOverOpen(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleGeneratePrepNotes = async () => {
    if (!id || isGenerateLoading) return;
    generatePrepNotes(id);
  };  

  const handleCardClick = () => {
    setIsSlideOverOpen(true);
  };

  useEffect(() => {
    if (prepNotes?.ai_analysis_status !== "InProgress") return;

    const intervalId = setInterval(() => {
      refetchPrepNotes();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [prepNotes?.ai_analysis_status, refetchPrepNotes]);

  useEffect(() => {
    if (isPrepNotesError) {
      const errorMessage =
        PrepNotesError?.response?.data?.errors ||
        "An unknown error occurred";
      showSnackbar("error", errorMessage);
    }
  }, [isPrepNotesError, PrepNotesError]);

  const notesHeaderTitle = hasMeetingTimePassed ? "Notes" : "Prep Notes";

  return (
      <>
          <Box>
            <Box className="prep-note-header">
              <Typography className="prep-note-header-title">
                {notesHeaderTitle}
              </Typography>

              {!userPublicNoteExists && isInteractionAttendee && !sharedWithMe && !expandPublicNote && !impersonating && (
                <Tooltip title="Add Public Note">
                  <button
                    className="add-note-btn"
                    onClick={() => setExpandPublicNote(true)}
                  >
                    <img src={addIcon} alt="Add Note Icon"/>
                    <Typography className="add_note">Add new note</Typography>
                  </button>
                </Tooltip>
              )}
            </Box>
            <Divider sx={{ my: 2, color: "#E5E5E5" }}/>
            { isPrepNotesEnabled && notes.length > 0 && prepNotes &&
               (prepNotes.ai_analysis_status === "completed" || !hasMeetingTimePassed) &&
              <Box sx={{width: "313px"}} >
                <PrepNotesCard
                    data={prepNotes}
                    onGenerate={handleGeneratePrepNotes}
                    onClick={handleCardClick}
                    isGenerateLoading={isGenerateLoading}
                    isInitialLoading={
                      (isPrepNotesLoading || isPrepNotesFetching) &&
                      prepNotes?.ai_analysis_status !== "completed"
                    }
                  />
              </Box>
            }
            <Box sx={{width: "100%", padding: "10px"}}>
             {expandPublicNote && (
                    <Note
                      isPrivate={false}
                      isEditMode={true}
                      expanded={expandPublicNote}
                      onExpandChange={setExpandPublicNote}
                    />
                  )}
            </Box>
            {publicNotes?.length > 0 || expandPublicNote ? (
              <Grid container spacing={2} padding={2}>
                {publicNotes?.map((note: NoteData) => {
                  return (
                    <Grid item xs={4}>
                      <NoteTileComponent
                        note={note}
                        onOpen={(note: any) => {
                          setSelectedNote(note);
                          setIsSlideOverOpen(true);
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box
                className="flex flex-col justify-center items-center w-full"
              >
                <img src={NotesIcon} alt="No Notes Icon" style={{ margin: "20px" }} />
                <Typography
                  variant="body1"
                  sx={{ color: theme.palette.primary.dark, fontFamily:'var(--font-family)', fontWeight:"500", fontSize:"14px" }}
                >
                    {sharedWithMe ? (
                      "No notes available."
                    ) : (
                      "You don't have any notes yet."
                    )}
                </Typography>
              </Box>
            )}
          </Box>
          <CustomConfirmation 
            title="Are you sure you want to delete this note?"
            width="350px"
            actionBtnLabel="Delete"
            cancelBtnLabel="Cancel"
            handleSuccess={handleDelete}
            handleClose={() => handleClose()}
            backgroundcolor={false}
            isOpen={openDialog} 
          />

      <ProgressSpinner showSpinner={showSpinner} />
        <SlideOver
        isOpen={isSlideOverOpen}
        onClose={() => {
          setIsSlideOverOpen(false);
          setSelectedNote(null);
        }}
      >
        {selectedNote ? (
          <NoteViewer
            note={selectedNote}
            isEditable={
              selectedNote.creatorEmail === loggedInUserEmail && !sharedWithMe
            }
            onUpdate={(noteId, updatedContent) => {
              mutateNoteUpdate(
                {
                  noteId,
                  text: updatedContent,
                  isUpdate: true,
                  isPrivate: false,
                },
                {
                  onSuccess: () => {
                    setSelectedNote((prev: any) =>
                      prev ? { ...prev, notes: updatedContent } : prev
                    );
                  },
                }
              );
            }}
            onDelete={(note) => {
              setNoteToDelete(note);
              setOpenDialog(true);
            }}
          />
        ) : prepNotes ? (
          <PrepNotesSlideOver data={prepNotes} interactionId={id || ""}/>
        ) : null}
      </SlideOver>
    </>
  );
};

export default Notes;
